import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    Telescope,
    Heart,
    ShieldCheck,
    Users,
    Leaf,
    MessagesSquare,
} from "lucide-react";
import { MaskedLines, FadeUp, Stagger, Item } from "../components/Reveal";
import { FloatingBee, HexPattern, Wave } from "../components/Bee";
import { BeeMark } from "../components/Logo";
import ContactBanner from "../components/ContactBanner";
import { SITE, IMAGES, TEAM } from "../data/site";

const VALUES = [
    {
        icon: Telescope,
        title: "Curiosity",
        copy: "Questions are celebrated here — every “why” opens a new project.",
    },
    {
        icon: Heart,
        title: "Kindness",
        copy: "We practice gentle words, helping hands and warm goodbyes.",
    },
    {
        icon: ShieldCheck,
        title: "Safety First",
        copy: "Secure spaces, certified staff and healthy routines, always.",
    },
    {
        icon: Users,
        title: "Inclusion",
        copy: "Every family and every bee belongs, exactly as they are.",
    },
    {
        icon: Leaf,
        title: "Nature Care",
        copy: "From garden beds to recycling crafts, we grow green hearts.",
    },
    {
        icon: MessagesSquare,
        title: "Family Partnership",
        copy: "Daily updates, open doors and honest, friendly conversations.",
    },
];

const GALLERY_TABS = [
    { id: "moments", label: "Busy Bees Moments" },
    { id: "indoor", label: "Indoor Spaces" },
    { id: "outdoor", label: "Outdoor Play" },
    { id: "art", label: "Art Studio" },
];

const MOMENT_ALTS = [
    "Busy Bees children sharing a cheerful outdoor picnic",
    "Busy Bees children celebrating Children's Day together",
    "Busy Bees children enjoying a Children's Day activity",
];

const TEAM_TONES = ["bg-honeysoft", "bg-sage", "bg-skysoft", "bg-peach", "bg-lav"];

export default function About() {
    useEffect(() => {
        document.title = `About Us — ${SITE.name}`;
    }, []);

    const [tab, setTab] = useState("moments");
    const gallery =
        tab === "moments"
            ? IMAGES.galleryMoments
            : tab === "indoor"
            ? IMAGES.galleryIndoor
            : tab === "outdoor"
              ? IMAGES.galleryOutdoor
              : IMAGES.galleryArt;

    return (
        <>
            {/* ---------- STORY HERO ---------- */}
            <section
                data-testid="about-story-hero"
                className="relative overflow-hidden pb-20 pt-28 sm:pt-36 lg:pb-28"
            >
                <HexPattern className="pointer-events-none absolute -left-28 top-24 h-[460px] w-[640px]" />
                <div className="wrap relative grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12">
                    <div>
                        <FadeUp>
                            <span className="eyebrow">Our Story</span>
                        </FadeUp>
                        <h1 className="mt-6 font-display text-[clamp(2.6rem,6vw,4.6rem)] font-semibold leading-[1.05] tracking-tight">
                            <MaskedLines
                                lines={[
                                    "Grown with love,",
                                    <>
                                        guided by{" "}
                                        <span className="text-honey-warm">
                                            wonder.
                                        </span>
                                    </>,
                                ]}
                            />
                        </h1>
                        <FadeUp delay={0.4}>
                            <p className="mt-6 max-w-lg text-lg leading-relaxed text-ink-muted">
                                Busy Bees began with a simple belief:
                                childhood is worth protecting. So we built a
                                place where days move at a child's pace —
                                sticky fingers, tall tales, puddle-jumping
                                questions and all.
                            </p>
                            <p className="mt-4 max-w-lg leading-relaxed text-ink-muted">
                                Our play-based, Reggio-inspired approach blends
                                cozy routines with open-ended discovery,
                                guided by teachers who treat every child like
                                their own. Families are our partners in the
                                hive — always welcome, always heard.
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.5}>
                            <div className="mt-7 flex flex-wrap gap-2.5">
                                {[
                                    "Play-based",
                                    "Reggio-inspired",
                                    "Family-first",
                                ].map((chip) => (
                                    <span
                                        key={chip}
                                        className="rounded-full border border-honey/40 bg-white px-4 py-2 text-sm font-medium text-honey-warm"
                                    >
                                        {chip}
                                    </span>
                                ))}
                            </div>
                        </FadeUp>
                    </div>
                    <div className="relative mx-auto w-full max-w-[540px] pb-10 pl-6 sm:pl-10">
                        <FadeUp>
                            <div className="overflow-hidden rounded-[2.5rem] border-8 border-white shadow-[0_30px_80px_rgba(185,125,10,0.2)]">
                                <img
                                    src={IMAGES.collageMain}
                                    alt="A teacher celebrating with a child at Busy Bees"
                                    className="aspect-[4/3] w-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-2 left-0 w-40 -rotate-6 overflow-hidden rounded-[1.75rem] border-4 border-white shadow-xl sm:w-52">
                                <img
                                    src={IMAGES.collageSmall}
                                    alt="Children building together in the classroom"
                                    className="aspect-square w-full object-cover"
                                />
                            </div>
                            <FloatingBee className="-top-8 right-4" />
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* ---------- VALUES ---------- */}
            <section data-testid="about-values-grid" className="wrap pb-16 lg:pb-24">
                <div className="max-w-xl">
                    <FadeUp>
                        <span className="eyebrow">What We Believe</span>
                    </FadeUp>
                    <FadeUp delay={0.08}>
                        <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                            Six seeds we plant every day
                        </h2>
                    </FadeUp>
                </div>
                <Stagger className="mt-10 grid gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
                    {VALUES.map((v) => (
                        <Item key={v.title} className="h-full">
                            <div
                                data-testid={`value-card-${v.title
                                    .toLowerCase()
                                    .replace(/[^a-z]+/g, "-")}`}
                                className="card-lift h-full rounded-3xl border border-ink/5 bg-white p-6 sm:p-7"
                            >
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-honeysoft">
                                    <v.icon className="h-5 w-5 text-honey-warm" />
                                </span>
                                <h3 className="mt-4 font-display text-xl font-semibold">
                                    {v.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                                    {v.copy}
                                </p>
                            </div>
                        </Item>
                    ))}
                </Stagger>
            </section>

            {/* ---------- TEAM ---------- */}
            <section data-testid="about-teachers-grid" className="wrap pb-16 lg:pb-24">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div className="max-w-xl">
                        <FadeUp>
                            <span className="eyebrow">Meet the Hive</span>
                        </FadeUp>
                        <FadeUp delay={0.08}>
                            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                                The keepers of the buzz
                            </h2>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.15}>
                        <p className="max-w-sm leading-relaxed text-ink-muted">
                            Meet the dedicated leaders and educators who make
                            every Busy Bees day feel welcoming and full of joy.
                        </p>
                    </FadeUp>
                </div>
                <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {TEAM.map((m, index) => (
                        <Item key={m.name} className="h-full">
                            <div
                                data-testid={`team-card-${m.id}`}
                                className="card-lift h-full rounded-3xl bg-cream p-3"
                            >
                                <div
                                    data-testid={`team-photo-placeholder-${m.id}`}
                                    className={`relative flex aspect-[4/4.4] items-center justify-center overflow-hidden rounded-b-2xl rounded-t-[8rem] ${TEAM_TONES[index % TEAM_TONES.length]}`}
                                >
                                    <HexPattern
                                        className="absolute inset-0 h-full w-full"
                                        stroke="#FFFFFF"
                                        opacity={0.34}
                                    />
                                    <BeeMark className="relative h-24 w-24 drop-shadow-sm" />
                                    <span className="absolute bottom-4 rounded-full bg-white/85 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-soft">
                                        Portrait coming soon
                                    </span>
                                </div>
                                <div className="p-3.5">
                                    <h3 className="font-display text-lg font-semibold">
                                        {m.name}
                                    </h3>
                                    <p className="mt-0.5 text-sm font-semibold text-honey-warm">
                                        {m.role}
                                    </p>
                                </div>
                            </div>
                        </Item>
                    ))}
                </Stagger>
            </section>

            {/* ---------- GALLERY ---------- */}
            <section data-testid="about-photo-gallery" className="relative">
                <Wave fill="#FFF9E6" />
                <div className="-mt-px bg-warm py-12 lg:py-16">
                    <div className="wrap">
                        <div className="flex flex-wrap items-end justify-between gap-6">
                            <div className="max-w-xl">
                                <FadeUp>
                                    <span className="eyebrow">
                                        Life at Busy Bees
                                    </span>
                                </FadeUp>
                                <FadeUp delay={0.08}>
                                    <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
                                        Where the magic happens
                                    </h2>
                                </FadeUp>
                            </div>
                            <FadeUp delay={0.15}>
                                <div className="flex flex-wrap gap-2">
                                    {GALLERY_TABS.map((t) => (
                                        <button
                                            key={t.id}
                                            data-testid={`gallery-tab-${t.id}`}
                                            onClick={() => setTab(t.id)}
                                            className={`rounded-full px-5 py-2.5 font-display text-sm font-semibold transition-all duration-300 ${
                                                tab === t.id
                                                    ? "bg-ink text-paper shadow-md"
                                                    : "border border-ink/10 bg-white text-ink/70 hover:border-honey hover:text-ink"
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </FadeUp>
                        </div>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={tab}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.35 }}
                                className="mt-8 grid gap-4 sm:grid-cols-3"
                            >
                                {gallery.map((src, i) => (
                                    <div
                                        key={src}
                                        data-testid={`gallery-image-${tab}-${i}`}
                                        className="group overflow-hidden rounded-3xl border-4 border-white shadow-md"
                                    >
                                        <img
                                            src={src}
                                            alt={
                                                tab === "moments"
                                                    ? MOMENT_ALTS[i]
                                                    : `Busy Bees ${
                                                          GALLERY_TABS.find(
                                                              (t) =>
                                                                  t.id ===
                                                                  tab,
                                                          )?.label
                                                      } ${i + 1}`
                                            }
                                            loading="lazy"
                                            className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                                                tab === "moments"
                                                    ? "aspect-[3/4] object-center"
                                                    : "aspect-[4/3]"
                                            }`}
                                        />
                                    </div>
                                ))}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
                <Wave fill="#FFF9E6" className="rotate-180" />
            </section>

            {/* ---------- VISIT US ---------- */}
            <ContactBanner
                testid="about-visit-us-section"
                title="Plan your visit"
                sub="The best way to know Busy Bees is to feel it in person. Call or email to book a tour — little ones welcome, of course."
            />
        </>
    );
}
